import compact from 'lodash/compact'
import find from 'lodash/find'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { memo, useContext, VFC } from 'react'
import styled from 'styled-components'

import SVGCheck from '@Assets/SVG/Check'
import { StyledSVGIcon, SVGIcon } from '@Components/Icons'
import { genericPDPBreakpoint } from '@Components/Product/Display/breakpoint'
import { BaseAttribute, Product } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'
import { formatMoney } from '@Globals/Money'
import { down, up } from '@Globals/Style/breakpoints'
import { useTranslation } from '@Globals/Translations'

export type DescriptionItem = Record<string, string>

interface ProductUSPsProps {
  buyTwoAllowed?: boolean
  buyTwoEnabled?: boolean
  product: Product
}

const OuterWrapper = styled.section`
  display: grid;
  font-size: .8rem;
  grid-template-columns: 1fr 1fr;
  margin: 0;

  ${up(genericPDPBreakpoint)} {
    display: block;
  }
`

const ListItem = styled.li<{ desktop?: boolean, responsive?: true }>(({ desktop, responsive }) => `
  position: relative;
  margin-bottom: .2em;

  ${responsive && `
    ${desktop
    ? `
        display: none;
        ${up(genericPDPBreakpoint)} {
          display: list-item;
        }
      `
    : `
        ${up(genericPDPBreakpoint)} {
          display: none;
        }
      `}
  `}
`)

const ListWrapper = styled.ul<{ hasIcons?: boolean }>(({ hasIcons, theme }) => `
  list-style: disc;
  padding: ${theme.padding.tablet} ${theme.padding.mobile} ${theme.padding.tablet} 2.25em;
  position: relative;

  ${up(genericPDPBreakpoint)} {
    padding: 0 ${theme.padding.mobile} 0 1.4em;
  }

  &:first-child:not(:last-child) {

    &:after {
      content: "";
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      background: ${theme.colors.greyLightest};
      width: 5px;
      transform: translateX(50%);

      ${up(genericPDPBreakpoint)} {
        display: none;
      }
    }

  }

  ${hasIcons && `
    // Using "down" here lets us define the defaults only once (above) and override them here, instead of having to
    // duplicate the "regular" styling for regular list itemss and icon-list-items on desktop (where the icons are
    // removed)
    ${down(genericPDPBreakpoint)} {
      list-style: none;
      padding-left: 0;
      padding-right: .5em;

      ${ListItem} {
        padding-left: 3em
      }
    }
  `}

  ${StyledSVGIcon} {
    display: inline-flex;
    position: absolute;
    left: 14px;
    width: 0.9rem;

    ${up(genericPDPBreakpoint)} {
      display: none;
    }

    *, & {
      color: ${theme.colors.primary};
      fill: currentColor;
    }
  }
`)

const STOCK_REVEAL_LIMIT = 10

export const ProductUSPs: VFC<ProductUSPsProps> = memo(({
  buyTwoAllowed,
  buyTwoEnabled,
  product,
}) => {
  const { locale = 'en-GB' } = useRouter()
  const {
    store: {
      config: { cheapestShippingPrice = undefined, freeShippingThreshold = undefined } = {},
      id: storeID = '',
    } = {},
  } = useContext(MetadataContext)
  const { t } = useTranslation()

  // TODO: this COULD be smarter!
  //  It would be nice to set this to true if
  //   - the item value itself is above the shipping threshold, or
  //   - the item value plus the current basket total (which we don't have) would exceed the shipping threshold
  //  But that's not the way we used to do things, and as I said, we don't have the data, but... maybe some day?
  const shippingIsFree = (product?.masterVariant?.price?.value || 0) >= (freeShippingThreshold?.value || 0)

  // Get the USPs from the product
  const uniqueSellingPoints = ((find(product?.masterVariant?.attributes, ['key', 'descriptionItems']) as BaseAttribute)?.value || []) as DescriptionItem[]

  // Get only the translated values we need
  const descriptionsToRender = compact(map(uniqueSellingPoints, descriptionItem => descriptionItem[locale]))

  // Calculate the shipping data thing
  const shippingInformation = (() => {
    if (shippingIsFree) {
      return t('products:FREE_SHIPPING_ON_THIS_ITEM')
    }

    if (!cheapestShippingPrice || !freeShippingThreshold) {
      return ''
    }

    return t(
      'products:SHIPPING_X_FREE_OVER_X',
      {
        arg1: formatMoney(storeID, locale, cheapestShippingPrice),
        arg2: formatMoney(storeID, locale, freeShippingThreshold),
      },
    )
  })()

  const quantity = product?.availability?.quantity || 0
  const showShippingInfo = quantity > 0
  const inStockLabel = quantity > STOCK_REVEAL_LIMIT
    ? t('products:PP_IN_STOCK_LABEL')
    : `${quantity} ${t('products:PP_LEFT_IN_STOCK_LABEL')}`

  return (
    <OuterWrapper>
      {showShippingInfo && (
        <ListWrapper hasIcons>
          <ListItem desktop responsive>
            {inStockLabel}
            {' '}
            &ndash;
            {' '}
            {shippingInformation}
          </ListItem>
          <ListItem responsive>
            <SVGIcon icon={SVGCheck} />
            {shippingInformation}
          </ListItem>
          <ListItem responsive>
            <SVGIcon icon={SVGCheck} />
            <strong>
              {inStockLabel}
            </strong>
          </ListItem>
          <ListItem responsive>
            <SVGIcon icon={SVGCheck} />
            {t('products:RETURN_POLICY_365_DAYS')}
          </ListItem>
        </ListWrapper>
      )}
      <ListWrapper>
        {buyTwoAllowed && !buyTwoEnabled && (
          <ListItem><strong>{t('products:PP_PRICE_IS_FOR_ONE_PIECE')}</strong></ListItem>
        )}
        {map(descriptionsToRender, item => (
          <ListItem key={item}>{item}</ListItem>
        ))}
      </ListWrapper>
    </OuterWrapper>
  )
})
